/**
 * Domain hooks built on TanStack Query.
 *
 * Naming convention:
 * - `useX()` → read query (auto-fetched, cached, revalidated on focus/reconnect)
 * - `useXMutations()` → bundle of create/update/remove/... mutations that
 *   invalidate the matching cache on success
 *
 * Pages should only ever import from this module + the service modules for
 * one-off side effects (e.g. AI chat).
 */
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { formatApiError } from "../lib/api";
import { invalidateAfterMutation, queryKeys } from "../lib/queryClient";
import { useInfiniteList } from "./useInfiniteList";
import {
  dashboardService,
  demoService,
  remindersService,
  fxService,
} from "../services/dashboardService";
import {
  cashAccountsService,
  profileService,
} from "../services/profileService";
import {
  paymentsService,
  receivablesService,
  movementsService,
  tagsService,
} from "../services/transactionsService";
import { insightsService } from "../services/insightsService";
import {
  debtsService,
  goalsService,
  investmentsService,
} from "../services/wealthService";
import {
  telegramService,
  whatsappService,
} from "../services/integrationsService";
import { chatService } from "../services/chatService";

const onError = (e) => toast.error(formatApiError(e?.response?.data?.detail));

// ============================================================
// Read queries
// ============================================================

export const useProfile = () =>
  useQuery({
    queryKey: queryKeys.profile,
    queryFn: () => profileService.get(),
  });

export const useCashAccounts = () =>
  useQuery({
    queryKey: queryKeys.cashAccounts,
    queryFn: () => cashAccountsService.list(),
  });

export const useDashboard = () =>
  useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: () => dashboardService.get(),
  });

export const useWeeklyReport = () =>
  useQuery({
    queryKey: queryKeys.weeklyReport,
    queryFn: () => dashboardService.weeklyReport(),
  });

export const useReceivables = () =>
  useQuery({
    queryKey: queryKeys.receivables,
    queryFn: () => receivablesService.list(),
  });

/**
 * Infinite-scroll variant of `useReceivables`. Returns a flat `items` array
 * plus `sentinelRef` to attach to a scroll-end div + `hasMore`/`loadMore`.
 * Backed by cursor-based pagination on the backend (stable under writes).
 * Filters compose: `status`, `search`, `date_from`, `date_to`.
 */
export const useReceivablesInfinite = (opts = {}) => {
  const { limit = 30, ...filters } = opts;
  return useInfiniteList({
    queryKey: ["receivables-infinite", filters],
    fetchPage: (p) => receivablesService.listPage({ ...p, ...filters }),
    limit,
  });
};

export const usePayments = () =>
  useQuery({
    queryKey: queryKeys.payments,
    queryFn: () => paymentsService.list(),
  });

export const usePaymentsInfinite = (opts = {}) => {
  const { limit = 30, ...filters } = opts;
  return useInfiniteList({
    queryKey: ["payments-infinite", filters],
    fetchPage: (p) => paymentsService.listPage({ ...p, ...filters }),
    limit,
  });
};

export const useMovements = (limit = 100) =>
  useQuery({
    queryKey: queryKeys.movements(limit),
    queryFn: () => movementsService.list(limit),
  });

export const useMovementsInfinite = (limit = 30) =>
  useInfiniteList({
    queryKey: ["movements-infinite"],
    fetchPage: (p) => movementsService.listPage(p),
    limit,
  });

export function useMovementsMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["movements"] });
    qc.invalidateQueries({ queryKey: ["movements-infinite"] });
    qc.invalidateQueries({ queryKey: ["history"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["cash-accounts"] });
  };
  return {
    update: useMutation({
      mutationFn: ({ id, payload }) => movementsService.update(id, payload),
      onSuccess: invalidate,
      onError,
    }),
    remove: useMutation({
      mutationFn: (id) => movementsService.remove(id),
      onSuccess: (data) => {
        invalidate();
        // ALSO invalidate the source-row lists so a restored payment/cobro
        // shows up in /Pagos or /Cobros immediately (otherwise the user
        // clicks 'Ir' and lands on a stale list).
        const qc2 = qc;
        qc2.invalidateQueries({ queryKey: ["payments"] });
        qc2.invalidateQueries({ queryKey: ["payments-infinite"] });
        qc2.invalidateQueries({ queryKey: ["receivables"] });
        qc2.invalidateQueries({ queryKey: ["receivables-infinite"] });
        qc2.invalidateQueries({ queryKey: ["debts"] });
        qc2.invalidateQueries({ queryKey: ["goals"] });
        qc2.invalidateQueries({ queryKey: ["investments"] });
        return data;
      },
      onError,
    }),
  };
}

export const useReport = (period = "weekly", start = null) =>
  useQuery({
    queryKey: ["report", { period, start }],
    queryFn: () => dashboardService.report(period, start),
  });

export const useHistory = (start = null, end = null) =>
  useQuery({
    queryKey: queryKeys.history(start, end),
    queryFn: () => dashboardService.history(start, end),
  });

export const useFxPnl = (start = null, end = null) =>
  useQuery({
    queryKey: queryKeys.fxPnl(start, end),
    queryFn: () => dashboardService.fxPnl(start, end),
  });

export const useNetWorthSeries = (days = 90, currency = null) =>
  useQuery({
    queryKey: queryKeys.netWorthSeries(days, currency),
    queryFn: () => dashboardService.netWorthSeries(days, currency),
  });

export const useForecast = (days = 30) =>
  useQuery({
    queryKey: queryKeys.forecast(days),
    queryFn: () => dashboardService.forecast(days),
  });

export const useReminderPreview = () =>
  useQuery({
    queryKey: queryKeys.reminders.preview,
    queryFn: () => remindersService.preview(),
  });

export const useLastReminder = () =>
  useQuery({
    queryKey: queryKeys.reminders.last,
    queryFn: () => remindersService.last(),
  });

export const useFxRates = () =>
  useQuery({
    queryKey: ["fx", "rates"],
    queryFn: () => fxService.rates(),
    staleTime: 60 * 60 * 1000, // 1h client cache (server already caches 24h)
  });

// Convenience: returns a stable `convert(amount, src, dst)` once rates are loaded.
// IMPORTANT: must be memoised on `rates` only, otherwise consumers that depend
// on it in a useEffect dependency array (e.g. AccountPickerDialog live preview)
// hit "Maximum update depth exceeded" because every parent render produced a
// new function reference and re-triggered the effect.
export const useFxConvert = () => {
  const { data } = useFxRates();
  const rates = data?.rates;  // keep the same reference as long as data is the same
  return useCallback((amount, src, dst) => {
    if (amount == null) return 0;
    if (!src || !dst || src === dst) return Number(amount);
    const r = rates || {};
    const s = r[String(src).toUpperCase()];
    const d = r[String(dst).toUpperCase()];
    if (!s || !d) return Number(amount);
    return (Number(amount) / s) * d;
  }, [rates]);
};

export const useTopTags = () =>
  useQuery({
    queryKey: ["tags", "top"],
    queryFn: () => tagsService.list(),
    staleTime: 60 * 1000,
  });

/** Line-item category aggregation for the dashboard donut. */
export const useLineItemCategories = (period = "month", scope = "expense") =>
  useQuery({
    queryKey: ["insights", "line-item-categories", period, scope],
    queryFn: () => insightsService.lineItemCategories(period, scope),
    staleTime: 60 * 1000,
  });

/** i18n labels for category ids. Cached aggressively — they never change. */
export const useCategoryLabels = () =>
  useQuery({
    queryKey: ["insights", "category-labels"],
    queryFn: () => insightsService.categoryLabels(),
    staleTime: 24 * 60 * 60 * 1000,
  });

export const useInvestments = (params = {}) =>
  useQuery({
    queryKey: [...queryKeys.investments, params],
    queryFn: () => investmentsService.list(params),
  });

export const useDebts = () =>
  useQuery({
    queryKey: queryKeys.debts,
    queryFn: () => debtsService.list(),
  });

export const useGoals = () =>
  useQuery({
    queryKey: queryKeys.goals,
    queryFn: () => goalsService.list(),
  });

export const useTelegramStatus = () =>
  useQuery({
    queryKey: queryKeys.integrations.telegram,
    queryFn: () => telegramService.status(),
  });

export const useWhatsAppStatus = () =>
  useQuery({
    queryKey: queryKeys.integrations.whatsapp,
    queryFn: () => whatsappService.status(),
    // Entitlements (`entitled` + `addon_required_plan`) can change at any
    // moment when an admin grants/revokes a bonus. We refetch on every
    // page mount AND when the browser tab regains focus so the banner
    // never lags behind the real DB state. The endpoint is cheap.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

export const useChatHistory = (limit = 50) =>
  useQuery({
    queryKey: queryKeys.chat.history,
    queryFn: () => chatService.history(limit),
  });

// ============================================================
// Mutation bundles
// ============================================================

/**
 * Generic mutation factory that invalidates the matching list query on success,
 * plus the dashboard (and weekly report) which depend on most data.
 *
 * Named with `use` prefix because it internally calls `useMutation` (React hooks rule).
 */
function useMakeMutations(qc, listKey, service, extras = {}) {
  // The list pages use `useInfiniteList` (queryKey: ["{kind}-infinite", filters])
  // for paginated rendering, while individual `useReceivables/Payments` hooks
  // use the flat `["{kind}"]` key. Mutations must invalidate BOTH or the
  // visible list won't refetch after create/update/delete/markPaid/collect.
  const infiniteKey = Array.isArray(listKey) && typeof listKey[0] === "string"
    ? [`${listKey[0]}-infinite`]
    : null;
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: listKey }),
      ...(infiniteKey ? [qc.invalidateQueries({ queryKey: infiniteKey })] : []),
      qc.invalidateQueries({ queryKey: ["history"] }),
      qc.invalidateQueries({ queryKey: ["movements-infinite"] }),
      qc.invalidateQueries({ queryKey: queryKeys.dashboard }),
      qc.invalidateQueries({ queryKey: queryKeys.weeklyReport }),
      qc.invalidateQueries({ queryKey: queryKeys.cashAccounts }),
      // Investments, debts and goals share side-effects with payments
      // and receivables (DCA bumps, debt-cuota decrement, goal contrib).
      // Without invalidating, /investments and /debts show stale values
      // after mark-paid/collect — even though the backend already updated.
      qc.invalidateQueries({ queryKey: queryKeys.investments }),
      qc.invalidateQueries({ queryKey: queryKeys.debts }),
      qc.invalidateQueries({ queryKey: queryKeys.goals }),
    ]);

  const create = useMutation({
    mutationFn: (payload) => service.create(payload),
    onSuccess: invalidate,
    onError,
  });
  const update = useMutation({
    mutationFn: ({ id, payload }) => service.update(id, payload),
    onSuccess: invalidate,
    onError,
  });
  const remove = useMutation({
    mutationFn: (id) => service.remove(id),
    onSuccess: invalidate,
    onError,
  });

  // Hooks must be called unconditionally in the same order: keep extras keys stable per page.
  const extraHooks = {};
  for (const [name, fn] of Object.entries(extras)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    extraHooks[name] = useMutation({
      mutationFn: fn,
      onSuccess: invalidate,
      onError,
    });
  }
  return { create, update, remove, ...extraHooks };
}

export function useReceivablesMutations() {
  const qc = useQueryClient();
  return useMakeMutations(qc, queryKeys.receivables, receivablesService, {
    // Accept either a bare id (legacy: payment already has account_id) or
    // {id, account_id} (picker dialog flow when the payment had no
    // funding source set yet).
    collect: (arg) => typeof arg === "string"
      ? receivablesService.collect(arg)
      : receivablesService.collect(arg.id, arg.account_id),
  });
}

export function usePaymentsMutations() {
  const qc = useQueryClient();
  return useMakeMutations(qc, queryKeys.payments, paymentsService, {
    markPaid: (arg) => typeof arg === "string"
      ? paymentsService.markPaid(arg)
      : paymentsService.markPaid(arg.id, { account_id: arg.account_id, debt_id: arg.debt_id }),
    postpone: ({ id, new_due_date }) => paymentsService.postpone(id, new_due_date),
  });
}

export function useInvestmentsMutations() {
  const qc = useQueryClient();
  return useMakeMutations(qc, queryKeys.investments, investmentsService);
}

export function useDebtsMutations() {
  const qc = useQueryClient();
  return useMakeMutations(qc, queryKeys.debts, debtsService, {
    pay: ({ id, amount }) => debtsService.pay(id, amount),
  });
}

export function useGoalsMutations() {
  const qc = useQueryClient();
  return useMakeMutations(qc, queryKeys.goals, goalsService, {
    contribute: ({ id, amount }) => goalsService.contribute(id, amount),
  });
}

export function useProfileMutations() {
  const qc = useQueryClient();
  return {
    update: useMutation({
      mutationFn: (payload) => profileService.update(payload),
      onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.profile }),
      onError,
    }),
    setWithdrawal: useMutation({
      mutationFn: ({ amount, reason }) => profileService.setWithdrawal({ amount, reason }),
      onSuccess: invalidateAfterMutation,
      onError,
    }),
  };
}

export function useCashAccountsMutations() {
  const qc = useQueryClient();
  return useMakeMutations(qc, queryKeys.cashAccounts, cashAccountsService);
}

export function useDemoMutations() {
  const qc = useQueryClient();
  return {
    seed: useMutation({
      mutationFn: () => demoService.seed(),
      onSuccess: () => qc.invalidateQueries(),
      onError,
    }),
    clear: useMutation({
      mutationFn: () => demoService.clear(),
      onSuccess: () => qc.invalidateQueries(),
      onError,
    }),
  };
}

export function useTelegramMutations() {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.integrations.telegram });
  return {
    link: useMutation({
      mutationFn: (code) => telegramService.link(code),
      onSuccess: invalidate,
      onError,
    }),
    unlink: useMutation({
      mutationFn: () => telegramService.unlink(),
      onSuccess: invalidate,
      onError,
    }),
  };
}

export function useWhatsAppMutations() {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.integrations.whatsapp });
  return {
    link: useMutation({
      mutationFn: (code) => whatsappService.link(code),
      onSuccess: invalidate,
      // 402 means the entitlement check failed. Invalidate anyway so a
      // freshly-granted bonus is picked up on the next render — the user
      // can retry the link without a full page reload.
      onError: (e) => { invalidate(); onError(e); },
    }),
    unlink: useMutation({
      mutationFn: () => whatsappService.unlink(),
      onSuccess: invalidate,
      onError,
    }),
  };
}

export function useRemindersMutations() {
  const qc = useQueryClient();
  return {
    sendNow: useMutation({
      mutationFn: () => remindersService.sendNow(),
      onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.reminders.last }),
      onError,
    }),
  };
}

export function useChatMutations() {
  const qc = useQueryClient();
  return {
    send: useMutation({
      mutationFn: (message) => chatService.send(message),
      onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.chat.history }),
      onError,
    }),
    confirm: useMutation({
      mutationFn: (items) => chatService.confirm(items),
      onSuccess: invalidateAfterMutation,
      onError,
    }),
    clearHistory: useMutation({
      mutationFn: () => chatService.clearHistory(),
      onSuccess: () => qc.setQueryData(queryKeys.chat.history, []),
      onError,
    }),
  };
}
